import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarClock, Mail, Plus, X, Trash2, Send, Loader2 } from "lucide-react";

const DAYS = { 0: "Sunday", 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday" };

export default function ScheduledReports({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/assistant/schedules")
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const toggle = async (s) => {
    await api.patch(`/assistant/schedules/${s.schedule_id}`, { enabled: !s.enabled });
    load();
  };
  const addRecipient = async (s) => {
    const email = (newEmail[s.schedule_id] || "").trim();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    await api.post(`/assistant/schedules/${s.schedule_id}/recipients`, { email });
    setNewEmail((p) => ({ ...p, [s.schedule_id]: "" }));
    load();
  };
  const removeRecipient = async (s, email) => {
    await api.delete(`/assistant/schedules/${s.schedule_id}/recipients/${encodeURIComponent(email)}`);
    load();
  };
  const del = async (s) => {
    await api.delete(`/assistant/schedules/${s.schedule_id}`);
    toast.success("Schedule removed");
    load();
  };
  const runNow = async (s) => {
    toast.promise(api.post(`/assistant/schedules/${s.schedule_id}/run-now`), {
      loading: "Sending report…",
      success: (r) => `Sent to ${r.data.recipients.join(", ")}`,
      error: "Send failed",
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="scheduled-reports">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
        <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <CalendarClock className="w-5 h-5" />
        </span>
        <div>
          <h2 className="text-base font-bold text-slate-900 leading-tight">Scheduled Reports</h2>
          <p className="text-xs text-slate-500">Ask the assistant to create one, then manage recipients here</p>
        </div>
      </div>

      <div className="p-5 space-y-4 max-h-[420px] overflow-y-auto">
        {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-300" />}
        {!loading && items.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-6">
            No scheduled reports yet. Try: “Email a weekly Monday sales & stock report to ross@thehottubsuperstore.co.uk”.
          </p>
        )}
        {items.map((s) => (
          <div key={s.schedule_id} className="border border-slate-200 rounded-xl p-4" data-testid={`schedule-${s.schedule_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">{s.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" />
                  Every {DAYS[s.day_of_week]} · {String(s.hour).padStart(2, "0")}:00 UK
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={s.enabled} onCheckedChange={() => toggle(s)} aria-label="Toggle schedule enabled" data-testid={`schedule-toggle-${s.schedule_id}`} />
                <button onClick={() => del(s)} aria-label="Delete schedule" className="text-slate-400 hover:text-rose-600 p-1" data-testid={`schedule-delete-${s.schedule_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {s.recipients.map((r) => (
                <span key={r} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded-md">
                  <Mail className="w-3 h-3 text-slate-400" />
                  {r}
                  <button onClick={() => removeRecipient(s, r)} className="ml-0.5 text-slate-400 hover:text-rose-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <Input
                value={newEmail[s.schedule_id] || ""}
                onChange={(e) => setNewEmail((p) => ({ ...p, [s.schedule_id]: e.target.value }))}
                placeholder="add recipient email"
                data-testid={`schedule-add-email-${s.schedule_id}`}
                className="h-8 text-sm"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient(s))}
              />
              <Button size="sm" variant="outline" onClick={() => addRecipient(s)} className="h-8 gap-1 shrink-0">
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
              <Button size="sm" onClick={() => runNow(s)} className="h-8 gap-1 shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`schedule-run-${s.schedule_id}`}>
                <Send className="w-3.5 h-3.5" /> Send now
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
